/* Read-only storefront adapter for Loyalino. No reward mutation or customer data here. */
(function () {
  'use strict';
  const number = value => new Intl.NumberFormat('da-DK', { maximumFractionDigits: 2 }).format(Number(value) || 0);
  const positive = value => Number.isFinite(Number(value)) && Number(value) >= 0;
  const currency = (value, code = 'DKK') => new Intl.NumberFormat('da-DK', { style: 'currency', currency: /^[A-Z]{3}$/.test(code) ? code : 'DKK', maximumFractionDigits: 0 }).format(Number(value) || 0);
  function benefitText(value) {
    const text = typeof value === 'string' ? value : value?.text || '';
    const shipping = text.match(/^(\d+) free shipping vouchers?$/i);
    if (shipping) return `${shipping[1]} ${Number(shipping[1]) === 1 ? 'kode' : 'koder'} til fri fragt ved opnået niveau`;
    if (text === 'Early access to new releases and sales') return 'Tidlig adgang til udvalgte drops og udsalg';
    return text;
  }
  function videoSource(source, mobile) {
    return mobile && source.mobileSrc ? source.mobileSrc : source.src;
  }
  function tierModel(tier, program) {
    const bySpend = program.vipTierLevelMethod === 'money_spent';
    const threshold = tier.inviteOnly || tier.assignOnly ? 'Kun efter invitation' : Number(tier.pointsRequired) === 0 ? 'Fra den dag, du bliver medlem' : bySpend ? `Fra ${currency(tier.pointsRequired, program.storeCurrency)} i køb` : `Fra ${number(tier.pointsRequired)} optjente point`;
    const benefits = [];
    const multiplier = Number(tier.multiplier) || 1;
    benefits.push(multiplier > 1 ? `${number(multiplier)} × point på dine køb` : 'Optjen point på dine køb');
    if (tier.discountType === 'percentage' && positive(tier.discountValue) && Number(tier.discountValue) > 0) benefits.push(`${number(tier.discountValue)}% medlemsrabat på dine ordrer`);
    if (tier.discountType === 'fixed_amount' && Number(tier.discountValue) > 0) benefits.push(`${currency(tier.discountValue, program.storeCurrency)} i medlemsrabat på dine ordrer`);
    if (tier.freeShipping) benefits.push('Fri fragt på dine ordrer');
    (Array.isArray(tier.benefits) ? tier.benefits : []).map(benefitText).filter(Boolean).forEach(text => benefits.push(text));
    return { id: String(tier.id), name: String(tier.name), threshold, benefits: [...new Set(benefits)] };
  }
  const earnCopy = {
    signup: ['Opret din konto', 'Din velkomst til klubben. Én gang pr. kunde.'],
    place_order: ['Shop dine favoritter', 'Point for hver 1 kr. i pointberettigede køb.'],
    birthday: ['Fejr din fødselsdag', 'Tilføj din fødselsdag på medlemskontoen i god tid.'],
    newsletter: ['Få nyt fra NeverMonday', 'Tilmeld dig nyhedsbrevet på medlemskontoen. Helt valgfrit.']
  };
  function earnPoints(rule, data) {
    // Loyalino's order webhook uses the program rate, not the label on its earn rule.
    return rule.type === 'place_order' && Number(data.pointsPerUnit) > 0 ? data.pointsPerUnit : rule.points;
  }
  function programModel(data) {
    if (!data || !Array.isArray(data.vipTiers) || !Array.isArray(data.earnRules) || !Array.isArray(data.redeemRewards)) throw new Error('Invalid Loyalino response');
    const tiers = data.vipEnabled === false ? [] : data.vipTiers.filter(t => t.id && t.name && positive(t.pointsRequired) && !(t.inviteOnly && t.showOnStorefront === false)).map(t => tierModel(t, data));
    const earn = Object.keys(earnCopy).map(type => data.earnRules.find(rule => rule.type === type && rule.enabled && positive(earnPoints(rule,data)))).filter(Boolean).map(rule => ({type:rule.type,amount:`${number(earnPoints(rule,data))} point`,title:earnCopy[rule.type][0],description:rule.type === 'birthday' && Number(rule.config?.birthdayMinDaysBefore) > 0 ? `Registrér din fødselsdag mindst ${number(rule.config.birthdayMinDaysBefore)} dage før.` : earnCopy[rule.type][1]}));
    const rewards = ['free_shipping','fixed_discount','percentage_discount'].map(type => data.redeemRewards.find(r => r.enabled && r.type === type && !r.variableRedemption && positive(r.pointsCost))).filter(Boolean).map(r => ({title:r.type === 'free_shipping' ? 'Fri fragt' : r.type === 'percentage_discount' ? `${number(r.discountValue)}% rabat` : `${currency(r.discountValue,data.storeCurrency)} rabat`,cost:`${number(r.pointsCost)} point`}));
    return {live:data.programLive === true,tiers,earn,rewards,welcome:earn.find(r => r.type === 'signup')?.amount || null};
  }
  function retryDelay(ms, signal) {
    return new Promise((resolve,reject)=>{
      const abort=()=>{clearTimeout(timer);reject(new DOMException('Aborted','AbortError'));};
      const timer=setTimeout(()=>{signal?.removeEventListener('abort',abort);resolve();},ms);
      if (signal?.aborted) abort();else signal?.addEventListener('abort',abort,{once:true});
    });
  }
  async function requestProgram(url, options, request=fetch, pause=retryDelay) {
    // The app occasionally returns an empty, paused-shaped response during
    // a transient backend read failure. Recheck it, but never invent active data.
    for(let attempt=0;attempt<3;attempt++) {
      if(options.signal?.aborted) throw new DOMException('Aborted','AbortError');
      try {
        const response=await request(url,options);
        if(!response.ok) throw new Error(`Loyalino ${response.status}`);
        const model=programModel(await response.json());
        if(model.live || attempt===2) return model;
      } catch(error) {if(options.signal?.aborted || error.name==='AbortError' || attempt===2) throw error;}
      await pause(attempt===0?300:900,options.signal);
    }
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { programModel, tierModel, benefitText, requestProgram, videoSource };
  if (typeof window === 'undefined' || !window.customElements || customElements.get('nm-members-club')) return;
  const make = (tag, text, className) => { const node = document.createElement(tag); if (text) node.textContent = text; if (className) node.className = className; return node; };

  class MembersClub extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      this.addEventListener('click', event => {
        const link = event.target.closest('[data-club-dashboard]');
        const canOpen = typeof window.Loyalino?.open === 'function' || typeof window.loyalinoToggle === 'function';
        const bootstrap = document.getElementById('loyalino-bootstrap-launcher');
        if (!link || !document.getElementById('loyalino-widget-root')) return;
        // The deployed app defers its runtime for guests until its launcher is
        // activated. Delegate to that native control, preserving its SRI-checked
        // loader and native error handling instead of loading a second runtime.
        if (!canOpen) {
          if (bootstrap && !bootstrap.disabled) {
            event.preventDefault();event.stopPropagation();bootstrap.click();
          }
          return;
        }
        event.preventDefault();
        // The native widget's document click handler closes clicks outside its
        // root. Our external launcher must not immediately trigger that handler.
        event.stopPropagation();
        const panel = document.getElementById('lw-panel');
        if (typeof window.Loyalino?.open === 'function') window.Loyalino.open();
        else if (!panel?.classList.contains('open') && !panel?.classList.contains('lw-open')) window.loyalinoToggle();
        if (typeof window.loyalinoNav === 'function') window.loyalinoNav(link.dataset.clubView || 'home');
      }, { signal:this.controller.signal });
      this.setupVideo();
      this.loadProgram();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.visibilityObserver?.disconnect();
      this.querySelector('video')?.pause(); this.initialized = false;
    }
    async loadProgram() {
      const status = this.querySelector('[data-club-status]');
      const timeout = setTimeout(() => this.fetchController?.abort(), 10000);
      this.fetchController = new AbortController();
      this.controller.signal.addEventListener('abort', () => this.fetchController.abort(), {once:true});
      try {
        const url = new URL('/apps/loyalty/data',window.location.origin);
        url.searchParams.set('shop',this.dataset.shop);
        url.searchParams.set('locale',this.dataset.locale || 'da');
        const model = await requestProgram(url,{credentials:'omit',cache:'no-store',headers:{Accept:'application/json'},signal:this.fetchController.signal});
        if (!this.isConnected) return;
        if (!model.live) {
          status.textContent = 'Members Club er midlertidigt sat på pause. Kontakt os, hvis du har spørgsmål til dine point.';
          this.querySelector('[data-club-ranks]').hidden = true;
          return;
        }
        status.hidden = true;
        const welcome = this.querySelector('[data-club-welcome]');
        if (model.welcome && welcome) {welcome.textContent = `${model.welcome} i velkomstbonus, når du opretter en konto.`;welcome.hidden=false;}
        const rankContainer=this.querySelector('[data-club-ranks]');
        this.querySelectorAll('[data-rank-id]').forEach(card=>{
          const tier=model.tiers.find(t=>t.id === card.dataset.rankId);
          card.hidden=!tier;
          if (!tier) return;
          card.querySelector('[data-rank-name]').textContent=tier.name;
          card.querySelector('[data-rank-threshold]').textContent=tier.threshold;
          card.querySelector('[data-rank-benefits]').replaceChildren(...tier.benefits.map(text=>make('li',text)));
        });
        // New app ranks still appear even before their optional artwork is configured.
        model.tiers.filter(t=>!Array.from(this.querySelectorAll('[data-rank-id]')).some(c=>c.dataset.rankId===t.id)).forEach(tier=>{
          const card=make('article',null,'nm-club__rank');card.dataset.rankId=tier.id;
          const copy=make('div',null,'nm-club__rank-copy');copy.append(make('h3',tier.name),make('p',tier.threshold,'nm-club__threshold'));
          const list=make('ul');list.append(...tier.benefits.map(t=>make('li',t)));copy.append(list);card.append(copy);rankContainer.append(card);
        });
        if (!model.tiers.length) this.querySelector('.nm-club__ranks').hidden = true;
        this.querySelector('[data-club-earn]').replaceChildren(...model.earn.map(rule=>{
          const el=make('article',null,'nm-club__earn-item');el.append(make('strong',rule.amount),make('h3',rule.title),make('p',rule.description));return el;
        }));
        this.querySelector('[data-club-earn-section]').hidden = !model.earn.length;
        this.querySelector('[data-club-rewards]').replaceChildren(...model.rewards.map(reward=>{
          const el=make('article',null,'nm-club__reward-item');el.append(make('h3',reward.title),make('p',reward.cost));return el;
        }));
        this.querySelector('[data-club-rewards-section]').hidden = !model.rewards.length;
      } catch (error) {
        if (!this.isConnected) return;
        status.hidden=false;status.textContent='De aktuelle pointkrav kunne ikke hentes. Du kan stadig se dine fordele på medlemskontoen.';
      } finally {clearTimeout(timeout);}
    }
    setupVideo() {
      const video=this.querySelector('video');
      if (!video) return;
      const reduce=window.matchMedia('(prefers-reduced-motion: reduce)');
      const mobile=window.matchMedia('(max-width: 699px)');
      let loaded=false;
      const play=()=>{
        if (!loaded) {video.querySelectorAll('source[data-src]').forEach(s=>{s.src=videoSource(s.dataset,mobile.matches);});video.load();loaded=true;}
        video.play().then(()=>{if(!video.paused) video.classList.add('is-playing');}).catch(()=>{video.classList.remove('is-playing');});
      };
      const sync=()=>{
        const autoAllowed=!reduce.matches&&!navigator.connection?.saveData;
        if (!document.hidden&&autoAllowed) play();else video.pause();
      };
      video.addEventListener('error',()=>{video.classList.remove('is-playing');},{signal:this.controller.signal});
      document.addEventListener('visibilitychange',sync,{signal:this.controller.signal});
      reduce.addEventListener('change',sync,{signal:this.controller.signal});
      mobile.addEventListener('change',()=>{
        // Keep only the correctly framed source active when the viewport changes.
        video.pause();video.classList.remove('is-playing');loaded=false;
        video.querySelectorAll('source').forEach(s=>s.removeAttribute('src'));video.load();sync();
      },{signal:this.controller.signal});
      // Continuous background loop; scrolling does not interrupt the animation.
      // Hidden tabs and visitor accessibility/data settings still take precedence.
      sync();
    }
  }
  customElements.define('nm-members-club',MembersClub);
})();
